import React, {useCallback, useContext, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useParams} from 'react-router-dom';

import {MessageContext} from 'src/contexts/message-context';
import {runUserIdsSearch} from 'src/openapi/admin/apis/UserSearchApi';

import {Item} from '@pb/kit-component-group';
import {StickyElement} from '@pb/kit-component-sticky-element';


import {
    UsersGroupsSearchResultController,
    UsersGroupsSearchResultView,
    userSearchResultStateActions,
    userSearchResultStateSelector,
} from '../users-groups-search-result';
import {UsersGroupsToolsRibbon} from '../users-tab/components/users-groups-tools-ribbon';

import {Header} from './components/header';
import {NoListPage} from './components/no-list-page';
import {parseUploadListFile, validateUploadListFile} from './upload-list-file-parser';

import type {ApiDispatchForToolkit} from 'src/openapi/admin/types';

export const UploadListTabWrapper = () => {
    const queryParams = useParams<{accountId: string, userType?: string}>();
    const dispatch = useDispatch();
    const apiDispatch = useDispatch<ApiDispatchForToolkit>();

    const {setErrorMessage} = useContext(MessageContext);
    const cacheKeys = useSelector(userSearchResultStateSelector);

    const [busy, setBusy] = React.useState(false);
    const [uniqueUserIds, setUniqueUserIds] = React.useState<number[]>([]);
    const [fileWasUploaded, setFileWasUploaded] = React.useState(false);

    useEffect(() => {
        if (queryParams.accountId) {
            dispatch(userSearchResultStateActions.setSearchCriteria({
                userIds: uniqueUserIds,
                accountIds: [+queryParams.accountId],
            }));
        }
    }, [uniqueUserIds, queryParams, dispatch]);

    const onLoadFile = useCallback(async (accepted: Array<File>) => {
        const file = accepted[0];
        if (!file) {
            return;
        }

        const accountId = Number(queryParams.accountId);
        if (!accountId || Number.isNaN(accountId)) {
            setErrorMessage('Invalid account ID.');
            return;
        }

        try {
            await validateUploadListFile(file);
        } catch (error) {
            if (error instanceof Error) {
                setErrorMessage(error.message);
            }
            return;
        }

        try {
            setBusy(true);
            const parsedData = await parseUploadListFile(file);

            const userEmails = parsedData.map(d => d.email).filter(Boolean) as string[];
            const userIds = parsedData.map(d => d.userId).filter(Boolean) as number[];

            const uniqueUsersIds: number[] = [];

            if (userEmails.length) {
                const result = await apiDispatch(runUserIdsSearch({
                    userIdsSearchRequest: {
                        searchCriteria: {userEmails, accountIds: [accountId]},
                    },
                }));

                if (result.payload.success) {
                    uniqueUsersIds.push(...result.payload.data);
                } else {
                    throw new Error(result.payload.data.message || 'Failed to fetch by email');
                }
            }

            if (userIds.length) {
                const result = await apiDispatch(runUserIdsSearch({
                    userIdsSearchRequest: {
                        searchCriteria: {userIds, accountIds: [accountId]},
                    },
                }));

                if (result.payload.success) {
                    uniqueUsersIds.push(...result.payload.data);
                } else {
                    throw new Error(result.payload.data.message || 'Failed to fetch by ID');
                }
            }

            setUniqueUserIds([...new Set(uniqueUsersIds)]);
            setFileWasUploaded(true);
        } catch (error) {
            if (error instanceof Error) {
                setErrorMessage(error.message);
            }
        } finally {
            setBusy(false);
        }
    }, [apiDispatch, queryParams, setErrorMessage]);

    const clearFilters = useCallback(() => {
        setUniqueUserIds([]);
    }, [setUniqueUserIds]);

    if (!queryParams.accountId) {
        return <div>Invalid or missing account ID.</div>;
    }

    if (!cacheKeys?.searchCriteria?.accountIds) {
        return <div>Loading data...</div>;
    }

    if (!fileWasUploaded) {
        return <NoListPage onLoadFile={onLoadFile} isLoading={busy}/>;
    }

    return (
        <UploadListTab
            accountId={+queryParams.accountId}
            clearDisabled={!uniqueUserIds.length}
            onClear={clearFilters}
            fileWasUploaded={fileWasUploaded}
        />
    );
};

const PLACE = 'Upload List';

type Props = {
    accountId: number,
    clearDisabled: boolean,
    onClear: () => void,
    fileWasUploaded?: boolean,
};

const UploadListTab = ({accountId, clearDisabled, onClear, fileWasUploaded}: Props) => {
    const isOwnUsersList = !clearDisabled && fileWasUploaded;

    return (
        <UsersGroupsSearchResultController place={PLACE}>
            {props => (
                <Item grow={1}>
                    <Header disabled={clearDisabled} onCLear={onClear}/>
                    <StickyElement className="sticky-tool-ribbon">
                        <UsersGroupsToolsRibbon
                            accountId={accountId}
                            place={PLACE}
                            adminId={props.adminId}
                            isLoading={props.isLoading}
                            getAllEntityIds={props.getAllEntityIds}
                            templates={props.templates}
                            activeSearchTemplate={props.activeSearchTemplate}
                            setActiveSearchTemplate={props.setActiveSearchTemplate}
                            loadTemplates={props.loadTemplates}
                            refreshResults={props.refreshResults}
                            loadActiveSearchTemplate={props.loadActiveSearchTemplate}
                        />
                    </StickyElement>
                    <UsersGroupsSearchResultView
                        getAllEntityIds={props.getAllEntityIds}
                        place={PLACE}
                        activeSearchTemplate={props.activeSearchTemplate}
                        isOwnUsersList={isOwnUsersList}
                    />
                </Item>
            )}
        </UsersGroupsSearchResultController>
    );
};
